import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  map,
  retryWhen,
  scan,
  switchMap,
  takeWhile,
  tap
} from 'rxjs/internal/operators';
import {SimpleSuggestion} from './simple-suggestion';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, of} from 'rxjs/index';
import {SuggestionComplex} from './suggestion-complex';
import {Router} from '@angular/router';
import {ResultWithHighlight} from './result-with-highlight';
import {AppConfigService} from '../../config/app-config.service';
import {ConfigStructure} from '../../config/config-structure.model';
import {TranslatorService} from '../../shared/translator.service';
import {AppUtils} from '../../shared/app-utils';
import {SuggestionsQuery} from './suggestions-query.model';
import {SuggestionsConfig} from '../../config/suggestions/suggestions-config.model';
import {forkJoin} from 'rxjs';
import {HtmlUtilsService} from '../../html-utils/html-utils.service';
import {TranslateService} from '../../shared/translate.service';
import {Focusable} from './focusable.interface';
import {SuggestionsConfigStructure} from '../../config/suggestions/suggestions-config-structure.model';
import {AutoCompleteAction} from '../../navbar/nav-search/nav-search.component';

@Component({
  selector: 'app-suggestions',
  templateUrl: './suggestions.component.html',
  styleUrls: ['./suggestions.component.css']
})
export class SuggestionsComponent implements OnInit {

  @Input() phraseField: FormControl;

  @Input() searchInText: boolean;

  @Input() searchActionNotifier: EventEmitter<boolean>;

  @Input() autoCompleteActionEmitter: EventEmitter<AutoCompleteAction>;

  @Output() hasSuggest = new EventEmitter<number>();

  @Output() hasFocus = new EventEmitter<boolean>();

  simpleSuggestions = new BehaviorSubject<SimpleSuggestion[]>([]);
  complexSuggestions = new BehaviorSubject<SuggestionComplex[]>([]);
  suggestionsConfig: SuggestionsConfig;
  suggestionsQuery: SuggestionsQuery[] = [];
  showWorks = false;
  showDescrs = false;
  showPublic = false;
  htmlUtils: HtmlUtilsService;
  focusableElements: Focusable[] = [];
  focusedSugg: Focusable;
  @ViewChild('list') private elList: ElementRef;

  constructor(private httpClient: HttpClient,
              private translator: TranslateService,
              private router: Router,
              private configService: AppConfigService,
              public translatorService: TranslatorService,
              htmlUtils: HtmlUtilsService) {
    this.htmlUtils = htmlUtils;
  }

  ngOnInit() {
    this.suggestionsConfig = this.configService.suggestionsConfig;
    this.suggestionsConfig.worksDefinitions
      .map(suggestionDefinition => new SuggestionsQuery(suggestionDefinition))
      .forEach(suggestionQuery => this.suggestionsQuery.push(suggestionQuery));
    this.suggestionsConfig.descriptorsDefinitions
      .map(suggestionDefinition => new SuggestionsQuery(suggestionDefinition))
      .forEach(suggestionQuery => this.suggestionsQuery.push(suggestionQuery));
    this.suggestionsConfig.publicDatasetDefinitions
      .map(suggestionDefinition => new SuggestionsQuery(suggestionDefinition))
      .forEach(suggestionQuery => this.suggestionsQuery.push(suggestionQuery));
    this.phraseField.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap((value: string) => this.handleAfterValueChange(value)),
      filter((value: string) => this.canQuery(value)),
      switchMap((query: string) => this.doQuery(query))
    ).subscribe(result => of());

    this.complexSuggestions.subscribe(values => {
      let worksTypes = this.suggestionsConfig.worksDefinitions.map(configStructure => configStructure.objectType);
      let descrsTypes = this.suggestionsConfig.descriptorsDefinitions.map(configStructure => configStructure.objectType);
      let publicTypes = this.suggestionsConfig.publicDatasetDefinitions.map(configStructure => configStructure.objectType);
      let resultsTypes = values.filter(value => value.hasResults()).map(value => value.type);

      this.showWorks = false;
      this.showDescrs = false;
      this.showPublic = false;
      resultsTypes.forEach(resultType => {
        if (worksTypes.includes(resultType)) {
          this.showWorks = true;
        }
        if (descrsTypes.includes(resultType)) {
          this.showDescrs = true;
        }
        if (publicTypes.includes(resultType)) {
          this.showPublic = true;
        }
      });
    });

    this.autoCompleteActionEmitter.subscribe(action => {
      switch (action) {
        case AutoCompleteAction.ARROW_UP:
          this.selectPrev();
          break;
        case AutoCompleteAction.ARROW_DOWN:
          this.selectNext();
          break;
        case AutoCompleteAction.ENTER:
          this.enterAction();
          break;
      }
    });
  }

  searchPhrase(suggestion: SimpleSuggestion) {
    let filters = AppUtils.phraseToFilter(suggestion.value).toURL();
    filters = filters.concat(',_type:-work');
    this.phraseField.setValue('');
    this.router.navigate(['/search/s'], {
      queryParams: {page: 1, filters, sort: '_score:desc'}
    });
    this.triggerSearchActionNotifier();
  }

  redirectTo(suggestionComplex: ResultWithHighlight) {
    const type = this.findTypeByResult(suggestionComplex);
    if (type) {
      let urlType;
      if (type.includes('work_by')) {
        urlType = 'work';
      } else {
        if (type === 'geographic_descriptor') {
          urlType = 'geographical_descriptor';
        } else {
          urlType = type;
        }
      }
      this.phraseField.setValue('');
      this.router.navigate([`/${urlType}/${suggestionComplex.id}`], {queryParams: {'suggestion': true}});
      this.triggerSearchActionNotifier();
    }
  }

  getPropertyValue(resultWithHighlight: ResultWithHighlight, config: ConfigStructure): string {
    return this.translatorService.translate(config, AppUtils.mapObjectToProperties(resultWithHighlight.item, false), false);
  }

  highlight(sentence: string): string {
    if (this.phraseField && this.phraseField.value) {
      return AppUtils.createHighlightByPattern(sentence, this.phraseField.value);
    }
    return sentence;
  }

  focusItem(suggestion?: Focusable, scroll: boolean = true) {
    this.focusableElements.forEach(el => el.clearFocus());
    this.focusedSugg = suggestion;
    if (suggestion) {
      suggestion.focus();
      if (scroll) {
        this.setScroll(this.findFocusedElement());
      }
    }
    this.hasFocus.emit(this.focusableElements.find(el => el.focused) != null);

  }

  private findFocusedElement() {
    return this.elList.nativeElement.querySelector('.main-page__suggestion-item.focus');
  }

  private setScroll(element?) {
    if (element) {
      let offsetTop = element.offsetTop;
      let elHeight = element.clientHeight;
      let scrollTop = this.elList.nativeElement.scrollTop;
      if (offsetTop + elHeight > this.elList.nativeElement.clientHeight) {
        this.elList.nativeElement.scrollTop = offsetTop;
      } else {
        this.elList.nativeElement.scrollTop = 0;
      }
    }
  }

  private findTypeByResult(suggestionComplex: ResultWithHighlight) {
    let complex = this.complexSuggestions.value.find(complex => complex.suggestions.find(result => result === suggestionComplex) != null);
    if (complex) {
      return complex.type;
    } else {
      return null;
    }
  }

  private selectPrev() {
    if (this.focusedSugg) {
      const index = this.focusableElements.indexOf(this.focusedSugg);
      if (index > -1) {
        if (index == 0) {
          this.focusItem();
        } else {
          this.focusItem(this.focusableElements[index - 1], true);
        }
      }
    }
  }

  private selectNext() {
    if (this.focusedSugg) {
      const index = this.focusableElements.indexOf(this.focusedSugg);
      if (index > -1 && index != this.focusableElements.length - 1) {
        this.focusItem(this.focusableElements[index + 1], true);
      }
    } else {
      this.focusFirst();
    }
  }

  private focusFirst() {
    if (this.focusableElements.length > 0) {
      this.focusItem(this.focusableElements[0], true);
    }
  }

  private triggerSearchActionNotifier() {
    if (this.searchActionNotifier) {
      this.searchActionNotifier.emit(true);
    }
  }

  private doQuery(query: string) {
    let query1 = this.httpClient.get(
      'apiUrl/omnis/search/suggestion/simple',
      {
        params: {
          'query': query,
          'maxSimpleSuggestions': this.configService.getConfig().maxSimpleSuggestions + ''
        }
      })
      .pipe(
        this.retryConfig(),
        map((rawResults: any[]) =>
          rawResults
            .filter(rawResult => rawResult != null)
            .map(rawResult => new SimpleSuggestion(rawResult))),
        this.errorConfig(),
      );

    let query2 = this.httpClient.post(
      'apiUrl/omnis/search/suggestion/complex', {
        'query': query,
        'configs': this.suggestionsQuery
      })
      .pipe(
        this.retryConfig(),
        map((rawResults: any[]) =>
          rawResults
            .filter(rawResult => rawResult != null)
            .map((rawResult: { type: string, suggestions: { item }[] }) => {
              if (rawResult.type.lastIndexOf('descriptor') < 0) {
                return new SuggestionComplex(rawResult);
              } else {
                return this.prepareDescriptorHighlight(rawResult);
              }
            })),
        this.errorConfig(),
      );
    forkJoin([
      query1,
      query2
    ]).subscribe(result => {
      let tempSimpleSugg = <SimpleSuggestion[]>result[0];
      let tempComplexSugg = <SuggestionComplex[]>result[1];

      this.simpleSuggestions.next(tempSimpleSugg);
      this.complexSuggestions.next(tempComplexSugg);

      this.fillFocusableElements();
      this.hasSuggest.next(this.focusableElements.length);
    });
    return of();
  }

  private prepareDescriptorHighlight(rawResult: { type: string; suggestions: { item }[] }) {
    let transformedSuggestions = [];
    rawResult.suggestions.forEach((suggest: { item }) => {
      let preferredName = suggest.item['preferredName'].replace(/<[^>]*>/g, '');
      let highlighted = this.highlight(preferredName);
      if (!this.containsHighlight(highlighted)) {
        if (suggest.item['secondaryName'] !== null) {
          let secondaryName = suggest.item['secondaryName'].replace(/<[^>]*>/g, '');
          let valHighlighted = this.highlight(secondaryName);
          if (this.containsHighlight(valHighlighted)) {
            transformedSuggestions.push({
              item: suggest.item,
              highlights: [valHighlighted],
              altHighlighted: true
            });
          }
        } else {
          if (highlighted.includes("<b>")) {
            transformedSuggestions.push({item: suggest.item, highlights: [highlighted]});
          }
        }
      } else {
        transformedSuggestions.push({item: suggest.item, highlights: [highlighted]});
      }
    });
    return new SuggestionComplex({type: rawResult.type, suggestions: transformedSuggestions})
  }

  private containsHighlight(highlighted): boolean {
    let count = 0;
    if (this.phraseField.value) {
      let split = this.phraseField.value.trim().split(" ");
      if (split) {
        count = split.length;
      }
    }
    return (highlighted.match(/<b>/g) || []).length == count;
  }

  private fillFocusableElements() {
    this.focusableElements = [];
    this.focusableElements = this.focusableElements.concat(this.simpleSuggestions.value);
    this.fillFocusableFromComplexByDefinition(this.suggestionsConfig.worksDefinitions);
    this.fillFocusableFromComplexByDefinition(this.suggestionsConfig.descriptorsDefinitions);
    this.fillFocusableFromComplexByDefinition(this.suggestionsConfig.publicDatasetDefinitions);
    this.focusItem();
  }

  private fillFocusableFromComplexByDefinition(definitions: SuggestionsConfigStructure[]) {
    definitions.forEach(definition => {
      AppUtils
        .getSuggestionComplexes(this.complexSuggestions.value, definition.objectType)
        .map(sugg => sugg.suggestions)
        .forEach(resultsWithHighlight => {
          this.focusableElements = this.focusableElements.concat(resultsWithHighlight);
        })
    });
  }

  private errorConfig() {
    return catchError(err => of(console.log(err)));
  }

  private retryConfig() {
    return retryWhen(err => err.pipe(
      switchMap((error) => {
        return of(error.status);
      }),
      scan(acc => acc + 1, 0),
      takeWhile(acc => acc < 4),
      delay(1000),
    ));
  }

  private canQuery(value: string) {
    return value != null && value.length >= 3 && !this.searchInText;
  }

  private handleAfterValueChange(value: string) {
    if (!this.canQuery(value)) {
      this.simpleSuggestions.next([]);
      this.complexSuggestions.next([]);
      this.hasSuggest.emit(0);
    }
  }

  private enterAction() {
    if (this.focusedSugg && this.focusedSugg.focused) {
      if (this.focusedSugg instanceof SimpleSuggestion) {
        this.searchPhrase(this.focusedSugg);
      } else if (this.focusedSugg instanceof ResultWithHighlight) {
        this.redirectTo(this.focusedSugg);
      }
    }
  }
}
